import { AdProviderTypes } from '@/types'

import { OutOfPageTypes, type AdType } from '../../types'
import { ProviderOutOfPageAd } from './provider-out-of-page-ad'

/**
 * Owner-side SSP tracker firing for GAM-delivered out-of-page slots (#2695).
 *
 * GAM preloads the winning prebid creative into a hidden container, so
 * creative-side tracker scripts either fire on renders the user never sees
 * (the playhop phantom-revenue incident) or have to reconstruct visibility
 * through frame boundaries they cannot always cross (SafeFrame). The provider
 * that OWNS the show moment has both facts the creative lacks:
 *
 * - the slot is actually shown — `showing` fires on `show()`/`makeVisible`;
 * - the slot is actually viewable — `viewable` descends from GPT
 *   `impressionViewable` (Active View: MRC 50%-for-1s, SafeFrame-aware).
 *
 * The whole contract lives in the shared bidder-side layer: `HeaderBidding`
 * marks owner-tracked auctions (`isOwnerTrackedAuction`) so the adapter skips
 * embedding tracker scripts in the creative markup, and
 * `GamProviderBase.applyPendingAuctionTargeting` attaches this helper —
 * concrete providers carry no tracking code.
 *
 * Both fires are gated on `headerBiddingAuctionResult.renderedBid` being OUR
 * banner bid: GAM may resolve the slot to a non-prebid line item, and
 * `renderedBid` is set only when Prebid actually rendered the winning creative
 * (`adRenderSucceeded`). Video bids are excluded — their trackers are injected
 * into the VAST by the SSP. Preload renders before `showing`, so the gate is
 * stable by the time either event dispatches.
 */

/**
 * How the viewable is confirmed, per provider:
 *
 * - `GAM_H5` — the banner's `viewable` event: its GPT `impressionViewable`
 *   arrives post-show (readiness comes from the slot-ready events), so it is a
 *   genuine Active View signal.
 * - `GAM_BANNER_INTERSTITIAL` — an owner-side MRC-style dwell after `showing`:
 *   that provider consumes `impressionViewable` on the hidden preload as its
 *   readiness signal and replays it synchronously at reveal, so its `viewable`
 *   event would count a sub-second show. Geometry is given (the wrapper is a
 *   fullscreen overlay while showing); the dwell cancels on tab-leave
 *   (`interaction_suspected`) and teardown, and requires the page to still be
 *   in the foreground when it elapses.
 *
 * A provider absent from this map gets no owner tracking (its auctions are
 * never marked, so creatives keep their embedded scripts).
 */
const OWNER_TRACKER_MODES: Partial<Record<AdProviderTypes, { viewableAfterShowingDwellMs?: number }>> = {
    [AdProviderTypes.GAM_H5]: {},
    [AdProviderTypes.GAM_BANNER_INTERSTITIAL]: { viewableAfterShowingDwellMs: 1000 },
}

/** Auctions whose trackers are fired owner-side instead of from the creative. */
export function isOwnerTrackedAuction(provider: AdProviderTypes, placement: AdType): boolean {
    return provider in OWNER_TRACKER_MODES && (OutOfPageTypes as readonly string[]).includes(placement)
}

/** Tracker fields the playgama adapter attaches to its bids. */
type TrackedBid = {
    bidder?: string
    mediaType?: string
    imp_tracker?: string
    viewable_tracker?: string
}

function firePixel(url: string) {
    new Image().src = url
}

/**
 * Both trackers are gated on Page Visibility, mirroring the creative-side
 * scripts: `showing` can dispatch with the tab already hidden (the ad was
 * prepared asynchronously and shown by automation), so the impression waits
 * for the first foreground moment while the ad is still up; the MRC dwell
 * counts only continuously-visible time — it starts in the foreground and
 * RESETS on `visibilitychange` → hidden. Everything stops on
 * `interaction_suspected`/`teardown` (`teardown` fires on `destroy()`).
 */
export function attachOwnerTrackers(banner: ProviderOutOfPageAd): void {
    const mode = OWNER_TRACKER_MODES[banner.provider]
    if (!mode) {
        return
    }

    let shown = false
    let impFired = false
    let viewableFired = false
    let dwell: ReturnType<typeof setTimeout> | null = null

    const pageVisible = () => document.visibilityState !== 'hidden'

    const trackedBid = (): TrackedBid | undefined => {
        const rendered = banner.headerBiddingAuctionResult?.renderedBid as TrackedBid | undefined
        return rendered?.bidder === 'playgama' && rendered.mediaType === 'banner' ? rendered : undefined
    }

    const tryFireImpression = () => {
        if (impFired || !shown || !pageVisible()) {
            return
        }
        const bid = trackedBid()
        if (bid?.imp_tracker) {
            impFired = true
            firePixel(bid.imp_tracker)
        }
    }

    const fireViewable = () => {
        if (viewableFired) {
            return
        }
        const bid = trackedBid()
        if (bid?.viewable_tracker) {
            viewableFired = true
            firePixel(bid.viewable_tracker)
        }
    }

    const stopDwell = () => {
        if (dwell) {
            clearTimeout(dwell)
            dwell = null
        }
    }

    const startDwell = () => {
        const dwellMs = mode.viewableAfterShowingDwellMs
        if (dwellMs === undefined || viewableFired || dwell || !shown || !pageVisible()) {
            return
        }
        dwell = setTimeout(() => {
            dwell = null
            if (pageVisible()) {
                fireViewable()
            }
        }, dwellMs)
    }

    const onVisibilityChange = () => {
        if (pageVisible()) {
            tryFireImpression()
            startDwell()
        } else {
            stopDwell()
        }
    }

    const stopAll = () => {
        shown = false
        stopDwell()
        document.removeEventListener('visibilitychange', onVisibilityChange)
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    banner.addEventListener('interaction_suspected', stopAll)
    banner.addEventListener('teardown', stopAll)

    banner.addEventListener('showing', () => {
        shown = true
        tryFireImpression()
        startDwell()
    })

    if (mode.viewableAfterShowingDwellMs === undefined) {
        // Event mode: the provider's `viewable` descends from a post-show GPT
        // `impressionViewable` (Active View), which never fires on a hidden
        // page — no extra gate needed.
        banner.addEventListener('viewable', fireViewable)
    }
}
